<script>
  import {onMount} from 'svelte';
  import Metadata from '$features/seo/Metadata.svelte';
  import {Button} from '$components/jera';
  import ImageComparison from '$components/ImageComparison.svelte';

  let {data} = $props();

  // Animation state
  let mounted = $state(false);
  let visibleSections = $state(new Set());

  onMount(() => {
    mounted = true;

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-animate');
            if (id && !visibleSections.has(id)) {
              visibleSections = new Set([...visibleSections, id]);
            }
          }
        });
      },
      {threshold: 0.15, rootMargin: '50px 0px'}
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  });

  // Luxury commerce pillars pulled from dash docs
  const features = [
    {
      phase: '01',
      label: 'Demand Capture',
      headline: 'DM concierge that qualifies every shopper',
      description:
        'Comment and DM triggers route guests into a guided conversation that captures product intent, sizing notes, and a compliant selfie submission in seconds.',
      highlights: [
        'Instagram and TikTok comment listeners with instant DM handoff',
        'Selfie capture with face detection and GDPR-friendly retention rules',
        'Merchandiser playbooks surface hero products automatically'
      ],
      roi: 'Save 15-20 hours/week on DM responses. Capture 40-50% more leads vs. business hours only.'
    },
    {
      phase: '02',
      label: 'AI Atelier',
      headline: 'Photo-real renderings tuned to your brand',
      description:
        'J\'ko pairs SDXL, ControlNet, and your brand prompts to deliver 1024x1024 imagery in under 20 seconds while preserving pose, lighting, and styling guardrails.',
      highlights: [
        'Pose and lighting preservation validated before delivery',
        'Carousel variants with recommended add-ons for each look',
        'Automated quality gates prevent off-brand results before they ship'
      ],
      roi: 'Save $5K-20K/month on traditional photoshoots. Reduce returns by 30-40% with accurate fit previews.'
    },
    {
      phase: '03',
      label: 'Clienteling Commerce',
      headline: 'Checkout without ever leaving the thread',
      description:
        'Live inventory links, one-tap checkout, and Stripe-powered payments appear the moment a guest is ready to buy, complete with credit management in the background.',
      highlights: [
        'Shopify sync for price, inventory, and variant availability',
        'Cart recovery and loyalty credit automations keep momentum high',
        'CSR handoff tools for styling teams who need to join the chat'
      ],
      roi: 'Reduce manual order entry by 95% = 10-15 hours/week saved. Increase conversion by 20-30%.'
    },
    {
      phase: '04',
      label: 'Revenue Intelligence',
      headline: 'Every engagement attributed back to revenue',
      description:
        'Dashboards surface try-on to purchase conversion, campaign lift, and product heatmaps so you know which items to restock and which creatives to scale.',
      highlights: [
        'ROI summaries with before and after benchmarks for leadership',
        'Engagement plus try-on conversion data for every post and DM',
        'Exports and webhooks for CRM, BI, or Slack alerts in real time'
      ],
      roi: 'Optimize ad spend by 40-60% by eliminating low-ROI campaigns. Make data-driven decisions 10× faster.'
    }
  ];

  const workflow = [
    {
      title: 'Sync your collection and rituals',
      description:
        'Connect Shopify or upload a curated CSV, choose the hero SKUs to spotlight, and define the keywords plus brand voice the concierge should honor.',
      actions: [
        'Select launch assortment and configure sizing notes',
        'Map comment triggers for Instagram, TikTok, and Facebook',
        'Establish compliance preferences and consent language'
      ]
    },
    {
      title: 'Train the digital stylist',
      description:
        'Run internal test flows using included credits to preview every prompt, adjust follow-up timing, and set escalation paths for VIP shoppers.',
      actions: [
        'Preview DM tone and imagery in sandbox mode',
        'Assign roles so stylists can step in when needed',
        'Lock guardrails for content safety and brand approvals'
      ]
    },
    {
      title: 'Go live across social',
      description:
        'Update profile bios, pin launch posts, and let the concierge capture demand. J\'ko qualifies requests, gathers selfies, and returns finished looks automatically.',
      actions: [
        'Use launch templates from dash.selify.ai to announce the experience',
        'Monitor the first wave of try-ons with real-time alerts',
        'Trigger instant follow-ups if a shopper pauses mid-conversation'
      ]
    },
    {
      title: 'Measure, iterate, and scale',
      description:
        'Review revenue intelligence dashboards to track conversion lift, export insights to your CRM, and adjust creative or incentives based on what performs.',
      actions: [
        'Export weekly ROI recaps for merchandising and finance',
        'Run A/B prompts using Growth and Pro testing suites',
        'Reward advocates with bonus credits or bespoke styling offers'
      ]
    }
  ];

  const technicalSpecs = [
    {
      category: 'AI Atelier',
      summary: 'Photoreal try-ons delivered in seconds.',
      items: [
        {label: 'Median render time', value: '12 seconds (p99 under 25s)'},
        {label: 'Model stack', value: 'SDXL + ControlNet with brand prompt tuning'},
        {label: 'Output', value: 'Up to 1024x1024 with carousel variants'},
        {label: 'Quality controls', value: 'Pose, lighting, and safety gates applied pre-delivery'}
      ]
    },
    {
      category: 'Commerce Stack',
      summary: 'Plug into the tooling your team already runs.',
      items: [
        {label: 'Commerce systems', value: 'Shopify, WooCommerce, custom catalogs via API'},
        {label: 'Payments', value: 'Stripe checkout links and invoicing'},
        {label: 'Campaign automations', value: 'Comment triggers, DM flows, credit rules'},
        {label: 'Team workflows', value: 'Roles, approvals, and live stylist handoff'}
      ]
    },
    {
      category: 'Governance & Trust',
      summary: 'Enterprise controls without slowing launch.',
      items: [
        {label: 'Data handling', value: 'GDPR and PIPEDA compliant, auto-delete after 24h'},
        {label: 'Security posture', value: 'Encryption in transit and at rest, SOC 2 in progress'},
        {label: 'Platform approvals', value: 'Official Meta Tech Provider, marketplace-ready'},
        {label: 'Reliability', value: '99.9% uptime SLA with proactive monitoring'}
      ]
    }
  ];
</script>

<Metadata
  title="How It Works — Selify AI Virtual Try-On"
  description="Discover how Selify transforms social commerce with AI-powered virtual try-ons. From customer selfie to completed purchase in under 60 seconds."
/>

<main class="how-it-works">
  <!-- Hero Section with animated background -->
  <section class="hero" class:mounted>
    <div class="hero-background">
      <div class="floating-particles">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>
    </div>

    <div class="hero-content">
      <span class="eyebrow">The Technology Behind the Experience</span>
      <h1>
        <span class="title-main">How Selify</span>
        <span class="title-accent">Transforms Social Commerce</span>
      </h1>
      <p class="hero-description">
        From Instagram comment to completed purchase in under 60 seconds. Here's the AI-powered infrastructure that makes it possible.
      </p>
      <div class="hero-stats">
        <div class="stat-item">
          <span class="stat-value">&lt;20s</span>
          <span class="stat-label">Generation</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">~92%</span>
          <span class="stat-label">Success Rate</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">24/7</span>
          <span class="stat-label">Automated</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Visual Demo Section -->
  <section
    class="demo-section"
    data-animate="demo"
    class:visible={visibleSections.has('demo')}
  >
    <div class="demo-container">
      <div class="demo-visual">
        <ImageComparison
          beforeSrc="/images/tryon-before.png"
          beforeAlt="Customer selfie before virtual try-on"
          afterSrc="/images/tryon-after.png"
          afterAlt="AI-generated virtual try-on result"
          initialSplit={50}
        />
      </div>
      <div class="demo-content">
        <span class="section-eyebrow">Live Example</span>
        <h2>Photo-Real Results in Seconds</h2>
        <p>
          Our AI preserves pose, lighting, and body language while seamlessly applying your products. Every detail engineered for conversion.
        </p>
        <ul class="demo-features">
          <li>Adaptive lighting and shadow matching</li>
          <li>Body pose preservation</li>
          <li>Garment physics simulation</li>
          <li>Brand-specific style transfer</li>
        </ul>
      </div>
    </div>
  </section>

  <!-- Features Grid -->
  <section class="experience-section">
    <div class="section-header">
      <span class="section-eyebrow">Commerce Playbook</span>
      <h2>Luxury concierge from first comment to conversion</h2>
      <p>
        Four integrated pillars show what happens behind the scenes and how your team uses Selify every day.
      </p>
    </div>

    <div class="experience-grid">
      {#each features as pillar, i}
        <article
          class="experience-card"
          data-animate={`feature-${i}`}
          class:visible={visibleSections.has(`feature-${i}`)}
          style="--delay: {i * 0.12}s"
        >
          <div class="experience-meta">
            <span class="experience-phase">{pillar.phase}</span>
            <span class="experience-label">{pillar.label}</span>
          </div>
          <h3>{pillar.headline}</h3>
          <p class="experience-description">{pillar.description}</p>
          <ul class="experience-list">
            {#each pillar.highlights as highlight}
              <li>{highlight}</li>
            {/each}
          </ul>
          {#if pillar.roi}
            <div class="roi-impact">
              <strong>💰 ROI Impact:</strong> {pillar.roi}
            </div>
          {/if}
        </article>
      {/each}
    </div>
  </section>

  <!-- Workflow Section -->
  <section
    class="playbook-section"
    data-animate="workflow"
    class:visible={visibleSections.has('workflow')}
  >
    <div class="section-header">
      <span class="section-eyebrow">How Teams Launch</span>
      <h2>From idea to live campaign in a weekend</h2>
      <p>
        Follow this launch sequence to move from pilot to full-scale roll-out while keeping every stakeholder aligned.
      </p>
    </div>

    <div class="playbook-grid">
      {#each workflow as item, i}
        <article
          class="playbook-card"
          data-animate={`workflow-step-${i}`}
          class:visible={visibleSections.has(`workflow-step-${i}`)}
          style="--delay: {i * 0.12}s"
        >
          <div class="playbook-heading">
            <span class="playbook-index">0{i + 1}</span>
            <h3>{item.title}</h3>
          </div>
          <p class="playbook-description">{item.description}</p>
          <ul class="playbook-actions">
            {#each item.actions as action}
              <li>{action}</li>
            {/each}
          </ul>
        </article>
      {/each}
    </div>
  </section>

  <!-- Technical Specifications -->
  <section
    class="specs-section"
    data-animate="specs"
    class:visible={visibleSections.has('specs')}
  >
    <div class="section-header">
      <span class="section-eyebrow">Operational Guarantees</span>
      <h2>Infrastructure your brand team can trust</h2>
      <p>
        Every promise here is documented inside dash.selify.ai with playbooks for engineering, compliance, and marketing teams.
      </p>
    </div>

    <div class="specs-grid">
      {#each technicalSpecs as spec, i}
        <div class="spec-card">
          <span class="spec-category">{spec.category}</span>
          <h3>{spec.summary}</h3>
          <dl class="spec-list">
            {#each spec.items as item}
              <div class="spec-item">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            {/each}
          </dl>
        </div>
      {/each}
    </div>
  </section>

  <!-- CTA Section -->
  <section class="cta-section" data-animate="cta" class:visible={visibleSections.has('cta')}>
    <div class="cta-content">
      <h2>See It in Action</h2>
      <p>
        Launch your first virtual try-on campaign in under 30 minutes. <em>No technical expertise required.</em>
      </p>
      <div class="cta-actions">
        <Button variant="primary" href="https://dash.selify.ai">Launch Dashboard</Button>
        <Button variant="secondary" href="mailto:hello@selify.ai">Request Demo</Button>
      </div>
    </div>
  </section>
</main>

<style lang="postcss">
  @reference '$theme';

  .how-it-works {
    @apply min-h-screen bg-base0;
  }

  /* Hero Section */
  .hero {
    @apply relative flex items-center justify-center overflow-hidden;
    min-height: 70vh;
    background: linear-gradient(180deg, var(--color-base1) 0%, var(--color-base0) 100%);
  }

  .hero-background {
    @apply absolute inset-0;
    z-index: 0;
  }

  .floating-particles {
    @apply absolute inset-0;
  }

  .particle {
    @apply absolute rounded-full;
    background: radial-gradient(circle, var(--color-base14) 0%, transparent 70%);
    opacity: 0.15;
    animation: float-around 20s infinite ease-in-out;
  }

  .particle-1 {
    width: 300px;
    height: 300px;
    top: 10%;
    left: 5%;
    animation-delay: 0s;
  }

  .particle-2 {
    width: 200px;
    height: 200px;
    top: 60%;
    right: 10%;
    animation-delay: -5s;
  }

  .particle-3 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    left: 50%;
    animation-delay: -10s;
  }

  .particle-4 {
    width: 250px;
    height: 250px;
    top: 30%;
    right: 30%;
    animation-delay: -7s;
  }

  .particle-5 {
    width: 180px;
    height: 180px;
    bottom: 40%;
    left: 20%;
    animation-delay: -12s;
  }

  @keyframes float-around {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    25% {
      transform: translate(30px, -30px) scale(1.1);
    }
    50% {
      transform: translate(-20px, 20px) scale(0.9);
    }
    75% {
      transform: translate(40px, 10px) scale(1.05);
    }
  }

  .hero-content {
    @apply relative z-10 mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 text-center;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .hero.mounted .hero-content {
    opacity: 1;
    transform: translateY(0);
  }

  .eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.3em] text-base4;
  }

  h1 {
    @apply flex flex-col gap-2 text-4xl font-semibold leading-tight md:text-5xl lg:text-6xl;
  }

  .title-main {
    @apply text-base6;
  }

  .title-accent {
    @apply bg-gradient-to-r from-base14 to-base14 bg-clip-text text-transparent;
    background-size: 200% auto;
    animation: shimmer 3s linear infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: 0% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  .hero-description {
    @apply max-w-2xl text-lg text-base4;
  }

  .hero-stats {
    @apply mt-8 grid grid-cols-3 gap-8;
  }

  .stat-item {
    @apply flex flex-col gap-1;
  }

  .stat-value {
    @apply text-3xl font-semibold text-base14;
  }

  .stat-label {
    @apply text-sm uppercase tracking-wide text-base4;
  }

  /* Demo Section */
  .demo-section {
    @apply mx-auto max-w-7xl px-6 py-24;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .demo-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .demo-container {
    @apply grid gap-12 lg:grid-cols-2 lg:items-center;
  }

  .demo-visual {
    @apply relative;
  }

  .demo-visual :global(.comparison-container) {
    @apply rounded-3xl shadow-2xl;
  }

  .demo-content {
    @apply flex flex-col gap-4;
  }

  .section-eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.3em] text-base14;
  }

  h2 {
    @apply text-3xl font-semibold text-base6 md:text-4xl;
  }

  .demo-content p {
    @apply text-lg text-base4;
  }

  .demo-features {
    @apply mt-4 flex flex-col gap-3;
  }

  .demo-features li {
    @apply flex items-center gap-3 text-base4;
  }

  .demo-features li::before {
    content: '✓';
    @apply flex h-5 w-5 items-center justify-center rounded-full bg-base14/20 text-sm text-base14;
  }

  /* Experience Section */
  .experience-section {
    @apply mx-auto max-w-7xl px-6 py-24;
  }

  .section-header {
    @apply mx-auto mb-16 flex max-w-3xl flex-col gap-4 text-center;
  }

  .section-header p {
    @apply text-lg text-base4;
  }

  .experience-grid {
    @apply grid gap-8 md:grid-cols-2;
  }

  .experience-card {
    @apply relative flex flex-col gap-6 rounded-[32px] border border-base3/30 p-8;
    background: linear-gradient(160deg, rgba(var(--color-base2-rgb), 0.9) 0%, rgba(var(--color-base0-rgb), 0.9) 60%, rgba(var(--color-base14-rgb), 0.08) 100%);
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.08);
    opacity: 0;
    transform: translateY(24px);
    transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .experience-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .experience-card::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 28px;
    border: 1px solid rgba(var(--color-base14-rgb), 0.08);
    pointer-events: none;
  }

  .experience-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.12);
  }

  .experience-meta {
    @apply flex items-baseline justify-between;
  }

  .experience-phase {
    @apply text-4xl font-semibold tracking-tight text-base14;
  }

  .experience-label {
    @apply text-xs font-semibold uppercase tracking-[0.3em] text-base4;
  }

  .experience-card h3 {
    @apply text-2xl font-semibold text-base6;
  }

  .experience-description {
    @apply text-base text-base4;
  }

  .experience-list {
    @apply flex flex-col gap-3 border-t border-base3/30 pt-4 text-sm text-base5;
  }

  .experience-list li {
    position: relative;
    padding-left: 1.5rem;
  }

  .experience-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.5rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: rgba(var(--color-base14-rgb), 0.8);
    box-shadow: 0 0 12px rgba(var(--color-base14-rgb), 0.6);
  }

  .roi-impact {
    @apply mt-4 p-4 rounded-lg text-sm;
    background: linear-gradient(135deg, rgba(var(--color-base14-rgb), 0.08), rgba(var(--color-base14-rgb), 0.04));
    border-left: 3px solid rgba(var(--color-base14-rgb), 0.6);
    color: var(--color-base6);
  }

  .roi-impact strong {
    @apply text-base14 font-semibold mr-1;
  }

  /* Playbook Section */
  .playbook-section {
    @apply mx-auto max-w-7xl px-6 py-24;
    background: linear-gradient(145deg, rgba(var(--color-base1-rgb), 0.85), rgba(var(--color-base0-rgb), 0.95));
    border-radius: 48px;
    border: 1px solid rgba(var(--color-base3-rgb), 0.2);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 20px 60px rgba(0, 0, 0, 0.08);
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .playbook-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .playbook-grid {
    @apply grid gap-6 md:grid-cols-2;
  }

  .playbook-card {
    @apply flex flex-col gap-4 rounded-3xl border border-base3/30 bg-base0/70 p-7;
    backdrop-filter: blur(16px) saturate(160%);
    -webkit-backdrop-filter: blur(16px) saturate(160%);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .playbook-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .playbook-card:hover {
    @apply -translate-y-1 border-base14/30 shadow-lg;
  }

  .playbook-heading {
    @apply flex items-baseline gap-3;
  }

  .playbook-index {
    @apply text-sm font-semibold uppercase tracking-[0.4em] text-base4;
  }

  .playbook-heading h3 {
    @apply text-xl font-semibold text-base6;
  }

  .playbook-description {
    @apply text-sm text-base4;
  }

  .playbook-actions {
    @apply flex flex-col gap-2 border-t border-base3/20 pt-4 text-sm text-base5;
  }

  .playbook-actions li {
    position: relative;
    padding-left: 1.25rem;
  }

  .playbook-actions li::before {
    content: '>';
    @apply absolute left-0 top-0 text-base14;
  }

  /* Specs Section */
  .specs-section {
    @apply mx-auto max-w-7xl px-6 py-24;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .specs-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .specs-grid {
    @apply grid gap-8 md:grid-cols-3;
  }

  .spec-card {
    @apply flex h-full flex-col gap-6 rounded-3xl border border-base3/30 bg-base1/60 p-8;
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .specs-section.visible .spec-card {
    opacity: 1;
    transform: translateY(0);
  }

  .spec-category {
    @apply text-xs font-semibold uppercase tracking-[0.4em] text-base4;
  }

  .spec-card h3 {
    @apply text-xl font-semibold text-base6;
  }

  .spec-list {
    @apply flex flex-col gap-4;
  }

  .spec-item {
    @apply flex flex-col gap-1 border-b border-base3/20 pb-4 last:border-0 last:pb-0;
  }

  .spec-item dt {
    @apply text-xs uppercase tracking-wide text-base4;
  }

  .spec-item dd {
    @apply text-sm font-medium text-base6;
  }

  /* CTA Section */
  .cta-section {
    @apply mx-auto max-w-5xl px-6 py-24;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .cta-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .cta-content {
    @apply flex flex-col items-center gap-6 rounded-[36px] border border-border bg-gradient-to-br from-base2 via-base1 to-base14/10 px-12 py-20 text-center shadow-lg md:px-16 md:py-24;
  }

  .cta-content h2 {
    @apply text-3xl font-semibold text-base6 md:text-4xl;
  }

  .cta-content p {
    @apply max-w-2xl text-lg text-base4;
  }

  .cta-actions {
    @apply mt-4 flex flex-wrap justify-center gap-4;
  }

  @media (max-width: 768px) {
    .hero {
      min-height: 60vh;
    }

    h1 {
      @apply text-3xl;
    }

    .hero-stats {
      @apply grid-cols-3 gap-4;
    }

    .features-grid {
      @apply grid-cols-1;
    }

    .specs-grid {
      @apply grid-cols-1;
    }
  }
</style>
